'use client';

import React from 'react';

export type CaptureCursor = { x: number; y: number };

const CROSS_SIZE_PX = 12;
const RULE_THICKNESS_PX = 2;
const CROSS_ARM_PX = CROSS_SIZE_PX / 2;

type MediaDisplayCaptureGuidesProps = {
  cursor: CaptureCursor | null;
};

/**
 * Figma Capture Thumbnail variant 2: brand 12px cross at the pointer plus
 * 2px rules to the capture-root edges (33% opacity), with a gap around the cross.
 */
export function MediaDisplayCaptureGuides({ cursor }: MediaDisplayCaptureGuidesProps) {
  if (!cursor) return null;

  const { x, y } = cursor;

  return (
    <div
      data-capture-overlay="true"
      data-testid="capture-crosshair-guides"
      className="pointer-events-none absolute inset-0 z-[10050]"
      aria-hidden="true"
    >
      <div
        className="absolute bg-[var(--text-brand,#0387fe)]"
        style={{
          left: x - CROSS_ARM_PX,
          top: y - RULE_THICKNESS_PX / 2,
          width: CROSS_SIZE_PX,
          height: RULE_THICKNESS_PX,
        }}
      />
      <div
        className="absolute bg-[var(--text-brand,#0387fe)]"
        style={{
          left: x - RULE_THICKNESS_PX / 2,
          top: y - CROSS_ARM_PX,
          width: RULE_THICKNESS_PX,
          height: CROSS_SIZE_PX,
        }}
      />
      <div
        className="absolute bg-[var(--surface-brand,#0387fe)] opacity-[0.33]"
        style={{
          left: 0,
          top: y - RULE_THICKNESS_PX / 2,
          width: Math.max(0, x - CROSS_ARM_PX),
          height: RULE_THICKNESS_PX,
        }}
      />
      <div
        className="absolute bg-[var(--surface-brand,#0387fe)] opacity-[0.33]"
        style={{
          left: x + CROSS_ARM_PX,
          top: y - RULE_THICKNESS_PX / 2,
          width: `calc(100% - ${x + CROSS_ARM_PX}px)`,
          height: RULE_THICKNESS_PX,
        }}
      />
      <div
        className="absolute bg-[var(--surface-brand,#0387fe)] opacity-[0.33]"
        style={{
          left: x - RULE_THICKNESS_PX / 2,
          top: 0,
          width: RULE_THICKNESS_PX,
          height: Math.max(0, y - CROSS_ARM_PX),
        }}
      />
      <div
        className="absolute bg-[var(--surface-brand,#0387fe)] opacity-[0.33]"
        style={{
          left: x - RULE_THICKNESS_PX / 2,
          top: y + CROSS_ARM_PX,
          width: RULE_THICKNESS_PX,
          height: `calc(100% - ${y + CROSS_ARM_PX}px)`,
        }}
      />
    </div>
  );
}
